import React, { useEffect, useState } from "react";
import { useGetAllStats } from "@api/statService";
import MaestroLoader from "@components/MaestroLoader";
import rLoadingWidget from "./ErrorLoadingWidget";
import { LoadingWidget } from "./LoadingWidget";
import { Box, Button, CardCover, DialogContent, DialogTitle, Divider, Grid, Modal, ModalDialog, ModalOverflow, Table, Typography } from "@mui/joy";
import StatWidgetCell, { StatWidgetTitle, StatWithIcon } from "./StatWidgetCell";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import WidgetButtons from "./WidgetButtons";
import { useGetAll } from "@api/crudService";
import ObjectPreview from "@components/objects/ObjectPreview";
import { formatDate } from "@utils/dates";

function HoursModal({title, endpoint, open, setOpen}) {
    return <StatModal
        title={title}
        endpoint={endpoint}
        open={open}
        setOpen={setOpen} columns={[
            {
                label: 'Person',
                cell: (row) => (
                    <ObjectPreview to={`/contacts/${row.contact.id}`} object={row.contact}>{row.contact.name}</ObjectPreview>
                )
            },
            {
                label: 'Time',
                cell: (row) => row.time_taken
            },
            {
                label: 'Date',
                cell: (row) => formatDate(row.volunteered_at)
            },
        ]}
    />
}

function TasksModal({title, endpoint, open, setOpen}) {
    return <StatModal
        title={title}
        endpoint={endpoint}
        open={open}
        setOpen={setOpen} columns={[
            {
                label: 'Task',
                cell: (row) => row.task_name
            },
            {
                label: 'Work Plan',
                cell: (row) => (
                    <ObjectPreview to={`/work-plans/${row.workplan_id}`} object={row.workplan}>{row.workplan.name}</ObjectPreview>
                )
            },
            {
                label: 'Due Date',
                cell: (row) => formatDate(row.due_date)
            },
        ]}
    />
}

function StatModal({title, endpoint, open, setOpen, columns}) {
    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetAll({
        queryKey: [endpoint],
        enabled: open,
    });
    // useGetAll
    // const [data, setData] = useState([]);

    return (
        <Modal open={open}
             onClose={() => setOpen(false)}
        >
            <ModalOverflow>
            <ModalDialog sx={{width: '100%', maxWidth: '800px'}}>
                <DialogTitle>{title}</DialogTitle>
                <Divider/>
                <DialogContent
                     sx={{
                     marginInline: 'var(--Divider-inset)',
                    //  paddingInline: 'calc(-1 * var(--Divider-inset))'
                }}>
                {isLoading === true && (<MaestroLoader style={{alignSelf: 'center'}} />)}
                {!data || !data.length && (<Typography sx={{px: 2, fontStyle: 'italic'}}>There is nothing to display.</Typography>)}
                {!!data && data.length > 0 && (<>
                    <Table
                    stickyHeader
                    stripe={'even'}
                    sx={{
                        tableLayout: 'auto'
                    }}
                    >
                        <thead>
                            <tr>
                                {columns.map(column => <th key={column.label}>{column.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map(column => <td key={`cell-${column.label}`}>{column.cell(row)}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>)}
                </DialogContent>
            </ModalDialog>
            </ModalOverflow>
        </Modal>
    );
}

export default function OverviewStatWidget(props) {
    const [completedOpen, setCompletedOpen] = useState(false);
    const [remainingOpen, setRemainingOpen] = useState(false);
    const [overdueOpen, setOverdueOpen] = useState(false);
    const [hoursOpen, setHoursOpen] = useState(false);

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetAllStats();

    if(isLoading) {
        return <LoadingWidget/>
    }

    if(isError) {
        return <ErrorLoadingWidget {...props} error={true} />
    }

    const simulateMouseUp = (fn) => {
        setTimeout(() => {
            fn();
        }, 200);
    }

    const editingSx = {
        '> *': {
            // transition: 'var(--mm-default-transition)',
        },
        ...(props.isEditing && {
            cursor: 'move',
            '> *': {
                filter: 'blur(2px)',
            },
        })
    };

    return (
        <Box container gap={2} display="grid" gridTemplateColumns={{xs: "repeat(3, 1fr)", md: "repeat(6, 1fr)", lg: "repeat(12, 1fr)"}} sx={{height: '100%'}}>
            <StatWidgetCell color="#1c996f" textColor="#fff" sx={editingSx}>
                <Button
                    className="react-grid-item-click-overlay"
                    // onMouseDown={() => {simulateMouseUp(() => alert('test'))}}
                    onClick={() => {setCompletedOpen(true)}}
                    aria-description="View completed tasks this month"
                ></Button>
                <TasksModal
                    title={'Completed Tasks'}
                    endpoint={'stats/completed'}
                    open={completedOpen}
                    setOpen={setCompletedOpen}
                />

                <StatWidgetTitle>Tasks completed this month</StatWidgetTitle>
                <StatWithIcon
                    text={data['tasks']['completed_this_month']}
                    icon={<CheckCircleOutlineRoundedIcon/>}
                />
            </StatWidgetCell>

            <StatWidgetCell color="#2a7a99" textColor="#fff" sx={editingSx}>
                <Button
                    className="react-grid-item-click-overlay"
                    // onMouseDown={() => {simulateMouseUp(() => alert('test'))}}
                    onClick={() => {setRemainingOpen(true)}}
                    aria-description="View remaining tasks this month"
                ></Button>
                <TasksModal
                    title={'Remaining Tasks'}
                    endpoint={'stats/remaining'}
                    open={remainingOpen}
                    setOpen={setRemainingOpen}
                />

                <StatWidgetTitle>Tasks remaining this month</StatWidgetTitle>
                <StatWithIcon
                    text={data['tasks']['remaining_this_month']}
                    icon={<FormatListNumberedRoundedIcon/>}
                />
            </StatWidgetCell>

            <StatWidgetCell color="#cc343a" textColor="#fff" sx={editingSx}>
                <Button
                    className="react-grid-item-click-overlay"
                    // onMouseDown={() => {simulateMouseUp(() => alert('test'))}}
                    onClick={() => {setOverdueOpen(true)}}
                    aria-description="View overdue tasks this month"
                ></Button>
                <TasksModal
                    title={'Overdue Tasks'}
                    endpoint={'stats/overdue'}
                    open={overdueOpen}
                    setOpen={setOverdueOpen}
                />

                <StatWidgetTitle>Overdue tasks</StatWidgetTitle>
                <StatWithIcon
                    text={data['tasks']['overdue']}
                    icon={<NotificationsActiveRoundedIcon/>}
                />
            </StatWidgetCell>

            {/* #224e75 */}
            <StatWidgetCell color="#384c5e" textColor="#fff" sx={editingSx}>
                <Button
                    className="react-grid-item-click-overlay"
                    // onMouseDown={() => {simulateMouseUp(() => alert('test'))}}
                    onClick={() => {setHoursOpen(true)}}
                    aria-description="View hours this month"
                ></Button>
                <HoursModal
                    title={'Volunteer Hours'}
                    endpoint={'stats/volunteer-hours'}
                    open={hoursOpen}
                    setOpen={setHoursOpen}
                />

                <StatWidgetTitle>Volunteer hours this month</StatWidgetTitle>
                <StatWithIcon
                    text={data['volunteer-hours']['total']}
                    icon={<ChecklistRoundedIcon/>}
                />
            </StatWidgetCell>

            <Box
                sx={theme => ({
                    position: 'absolute',
                    zIndex: 100,
                    top: theme.spacing(1),
                    right: theme.spacing(1),
                    padding: 0,
                    backgroundColor: theme.palette.background.surface,
                    borderRadius: theme.radius.sm,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'var(--mm-default-transition)',
                })}

                style={
                    props.isEditing ? {
                        opacity: 1,
                        pointerEvents: 'all',
                    } : {}
                }
            >
                <WidgetButtons
                    widget={props.widget}
                    updateWidget={props.updateWidget}
                    isEditing={props.isEditing}
                    onEdit={props.onEdit}
                    onRemove={props.onRemove}
                    floating
                />
            </Box>
        </Box>
    );
}
